import React, { useEffect, useState } from 'react';
import { Form, Spin } from 'components/ds';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../PageContainer/PageContainer';
import { FormWrapper } from '../../../../components/Css';
import ProgressStepper from '../../../../components/ProgressStepper';
import { useGetTemplate } from 'modules/account/configs/SchemaConfig';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { FormFields, FormFooter, ProgressStepperWrapper, StyledForm } from './DynamicForm.styles';
import { DynamicFormProps, FormItemProps } from '../../_types/dynamicFormInterfaces.types';
import { renderSelectItem } from '../renderSelectItem/renderSelectItem';
import { renderInputItem } from '../renderInputItem/renderInputItem';
import { shouldDisableField } from '../../utils/shouldDisableField';
import { useExistingIdentifiers } from '../../context/ExistingIdentifiersContext';
import { useDynamicFieldsStorage } from '../../hooks/useDynamicFieldsStorage';
import { useGAEvents } from '../../../../hooks/useGoogleAnalytics';

export interface ExistingIdentifiers {
  email: boolean;
  phoneNumber: boolean;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
                                                          pageName,
                                                          onSubmit,
                                                          initialValues,
                                                          form: externalForm,
                                                          children,
                                                          loadingFields = {}
                                                        }) => {
  const [internalForm] = Form.useForm();
  const form = externalForm || internalForm;
  const navigate = useNavigate();
  const {isLoading, data: pageConfig = {}} = useGetTemplate(pageName);
  const [isFormValid, setIsFormValid] = useState(false);
  const {updateStorageData, registrationData} = useDynamicFieldsStorage();
  const { events } = useGAEvents();
  
  const formValues = Form.useWatch([], form);
  const {existingIdentifiers, isVerifying} = useExistingIdentifiers();
  const hasExistingIdentifiers = existingIdentifiers.email || existingIdentifiers.phoneNumber;
  
  const formatDateForInput = (dateValue: string): string => {
    if (!dateValue) return '';
    
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      
      const date = new Date(dateValue);
      
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      return '';
    }
  };
  
  const processInitialValues = (values: any): any => {
    if (!values) return values;
    
    const processed = {...values};
    
    Object.keys(processed).forEach(key => {
      const value = processed[key];
      
      if (value && typeof value === 'string' && value.includes('T') && value.includes('Z')) {
        const formatted = formatDateForInput(value);
        
        if (formatted) {
          processed[key] = formatted;
        }
      }
    });
    
    return processed;
  };
  
  const getStorageKey = (pageName: string): keyof typeof registrationData | null => {
    const keyMapping: { [key: string]: keyof typeof registrationData } = {
      'personal': 'personalInfo',
      'contact': 'contactInfo',
      'address': 'addressInfo',
      'additionalInfo': 'additionalInfo'
    };
    
    return keyMapping[pageName] || null;
  };
  
  // Evento GA4: Início do formulário dinâmico
  React.useEffect(() => {
    if (pageName) {
      events.formStart(`dynamic_form_${pageName}`);
    }
  }, [pageName, events]);
  
  useEffect(() => {
    const allFields = pageConfig.formFields || [];
    
    const fieldsToValidate = allFields.filter((field: any) => {
      const userData = initialValues || {};
      const isDisabled = shouldDisableField(field.name, userData, field.disabled);
      const fieldValue = form.getFieldValue(field.name);
      
      return !(isDisabled && (fieldValue && fieldValue.trim() !== ''));
      
    });
    
    const fieldNamesToValidate = fieldsToValidate.map((field: any) => field.name);
    
    form.validateFields(fieldNamesToValidate, {validateOnly: true}).then(
      () => {
        setIsFormValid(true);
      },
      () => {
        setIsFormValid(false);
      }
    );
  }, [formValues, form, pageConfig.formFields, initialValues]);
  
  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      setTimeout(() => {
        const processedValues = processInitialValues(initialValues);
        form.setFieldsValue(processedValues);
        setTimeout(() => {
          form.getFieldsValue();
        }, 100);
      }, 100);
    } else {
      const storageKey = getStorageKey(pageName);
      const savedData = storageKey ? registrationData[storageKey] : null;
      
      if (savedData) {
        setTimeout(() => {
          const processedData = processInitialValues(savedData);
          form.setFieldsValue(processedData);
        }, 100);
      }
    }
  }, [form, initialValues, pageName, registrationData]);
  
  const renderFormItem = (item: FormItemProps) => {
    const userData = initialValues || {};
    
    const isDisabled = shouldDisableField(
      item.name,
      userData,
      item.disabled
    );
    
    switch (item.type) {
      case 'select':
        return renderSelectItem(item, isDisabled);
      default:
        return renderInputItem(item, isDisabled, loadingFields[item.name], form);
    }
  };
  
  const onHandleSubmit = async (values: any) => {
    try {
      ApiLocalStorage.set(pageName, values);
      
      const storageKey = getStorageKey(pageName);
      
      if (storageKey) {
        updateStorageData(storageKey, values);
      }
      
      if (onSubmit) {
        await onSubmit();
      }
      
      // Evento GA4: Sucesso no envio do formulário dinâmico
      events.formSubmit(`dynamic_form_${pageName}`, true);
      
      navigate(pageConfig.navigateTo());
    } catch (error) {
      // Evento GA4: Erro no envio do formulário dinâmico
      events.formSubmit(`dynamic_form_${pageName}`, false);
      events.error('dynamic_form_error', error instanceof Error ? error.message : 'Erro no formulário dinâmico');
      throw error;
    }
  };
  
  if (isLoading) {
    return (
      <PageContainer title="">
        <FormWrapper>
          <Spin style={{marginBottom: '65px'}} size="large"/>
        </FormWrapper>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer
      title={pageConfig.title || 'Título'}
      navigateBack={pageConfig?.navigateBack?.()}
      navigateClose
      thereIsButton
      buttonText={pageConfig.buttonLabel || 'Próximo'}
      onClick={() => form?.submit()}
      loading={isLoading}
      disabled={!isFormValid || hasExistingIdentifiers || isVerifying}
    >
      <ProgressStepperWrapper>
        <ProgressStepper
          totalSteps={pageConfig.totalSteps}
          currentStep={pageConfig.currentStep}
        />
      </ProgressStepperWrapper>
      
      <FormWrapper>
        <StyledForm
          form={form}
          name="form"
          layout="vertical"
          onFinish={onHandleSubmit}
        >
          <FormFields>
            {(pageConfig.formFields || []).map((item: FormItemProps) =>
              renderFormItem(item)
            )}
          </FormFields>
          
          <FormFooter>
            {children}
          </FormFooter>
        </StyledForm>
      </FormWrapper>
    </PageContainer>
  );
};
